import type { StorageLike } from '@vueuse/core';

const memory = new Map<string, string>();

/**
 * localStorage afgeschermd met try/catch + in-memory fallback.
 *
 * In afgeschermde/kiosk-omgevingen (bv. sommige Citrix-policies) kan localStorage
 * geblokkeerd zijn of een error gooien. Deze wrapper zorgt dat de app dan niet crasht
 * maar terugvalt op een sessie-lokale Map (en daarmee op de defaults).
 *
 * Let op: dit dekt *geblokkeerde* opslag af, niet een *profielwipe* tussen Citrix-sessies.
 */
export const safeLocalStorage: StorageLike = {
  getItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return memory.has(key) ? (memory.get(key) ?? null) : null;
    }
  },
  setItem(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      memory.set(key, value);
    }
  },
  removeItem(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      memory.delete(key);
    }
  },
};

/**
 * Gelaagde, afgeschermde opslag voor Citrix/kiosk-omgevingen.
 *
 * Probeert achtereenvolgens localStorage → sessionStorage → in-memory Map. Elke laag is
 * apart in try/catch verpakt, zodat een geblokkeerde of vol geraakte laag automatisch
 * doorvalt naar de volgende. Schrijft naar de eerste werkende laag; leest in dezelfde
 * volgorde tot er een waarde gevonden wordt.
 *
 * Let op: dit dekt *geblokkeerde/partiële* opslag en quota-fouten af, niet een
 * *profielwipe* tussen Citrix-sessies (dan sneuvelen alle client-side lagen samen).
 */
export const resilientStorage: StorageLike = {
  getItem(key: string): string | null {
    try {
      const value = window.localStorage.getItem(key);
      if (value !== null) return value;
    } catch { /* val door naar sessionStorage */ }
    try {
      const value = window.sessionStorage.getItem(key);
      if (value !== null) return value;
    } catch { /* val door naar memory */ }
    return memory.has(key) ? (memory.get(key) ?? null) : null;
  },
  setItem(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
      return;
    } catch { /* val door naar sessionStorage */ }
    try {
      window.sessionStorage.setItem(key, value);
      return;
    } catch { /* val door naar memory */ }
    memory.set(key, value);
  },
  removeItem(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch { /* negeer */ }
    try {
      window.sessionStorage.removeItem(key);
    } catch { /* negeer */ }
    memory.delete(key);
  },
};
